import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { SectionCard, EmptyState } from "@/components/common/ui";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps, seriesColor } from "@/lib/chart";
import { axisINR } from "@/lib/format";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectSeparator,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
  PieChart, Pie,
} from "recharts";
import {
  ChevronDown, BarChart3, Layers, MonitorSmartphone, Building2, Eye, Zap,
  Download, SlidersHorizontal, ArrowUp, ArrowDown, Minus, Search, RotateCcw, MousePointerClick,
} from "lucide-react";
import {
  agencyChannel as _autoChannel, resolveAgencyChannel, isChannelOverridden, groupSum, topNWithOthers, orderBy,
  orderedPeriods, periodKey,
  VERTICAL_GRAPH_ORDER, AGENCY_CHANNEL_ORDER, AGENCY_CHANNEL_HINT,
} from "@/lib/spendClassify";

const DIMS = [
  { key: "cost_bucket", label: "Cost Bucket" },
  { key: "vertical", label: "Vertical" },
  { key: "project", label: "Project" },
  { key: "platform", label: "Platform" },
  { key: "vendor", label: "Vendor" },
  { key: "agency_channel", label: "Agency Channel" },
];
const AUTO = "__auto__";

// keyFn for a dimension — Agency Channel is derived (override-aware).
function keyForDim(d) {
  if (d === "agency_channel") return (r) => (r.platform === "Agency" ? resolveAgencyChannel(r) : null);
  return (r) => r[d];
}

function HBar({ rows, total, accent, gradId, money, testId, height, onBarClick }) {
  const data = (rows || []).filter((r) => r.spend > 0);
  if (!data.length) return <div className="py-8"><EmptyState title="No spend" message="Nothing matches the current filters." /></div>;
  const useGrad = !!(accent && gradId);
  const tip = (v) => `${money(v)} \u00b7 ${total ? Math.round((v / total) * 100) : 0}%`;
  const clickable = typeof onBarClick === "function";
  const fire = (d) => { if (clickable) onBarClick(d?.payload?.name ?? d?.name); };
  return (
    <div style={{ height: height || Math.max(180, data.length * 34) }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 2, right: 66, left: 4, bottom: 0 }}>
          {useGrad && (
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                <stop offset="100%" stopColor={accent} stopOpacity={1} />
              </linearGradient>
            </defs>
          )}
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
          <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
          <YAxis type="category" dataKey="name" width={124} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} interval={0} />
          <Tooltip content={<ChartTooltip formatter={tip} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
          <Bar dataKey="spend" name="Spend" radius={[0, 5, 5, 0]} barSize={16} isAnimationActive={false}
            onClick={fire} style={clickable ? { cursor: "pointer" } : undefined}>
            {data.map((_, i) => <Cell key={i} fill={useGrad ? `url(#${gradId})` : seriesColor(i)} />)}
            <LabelList dataKey="spend" position="right" formatter={(v) => money(v)} style={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} className="tabular-nums" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Tile({ icon: Icon, title, accent, hint, children, testId, action, className }) {
  return (
    <div className={"rounded-xl border bg-card p-4 " + (className || "")} data-testid={testId}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${accent}1f`, color: accent }}><Icon className="h-4 w-4" /></span>
          {title}
        </p>
        {action}
      </div>
      {hint && <p className="mb-2 text-[11px] text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

function DeltaBadge({ delta, prev, money }) {
  if (prev === null || prev === undefined) {
    return <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground" data-testid="delta-none">new</span>;
  }
  const up = delta > 0.5;
  const down = delta < -0.5;
  const pct = prev ? Math.round((delta / prev) * 100) : (delta > 0 ? 100 : 0);
  const color = up ? "hsl(var(--danger))" : down ? "hsl(var(--success))" : "hsl(var(--muted-foreground))";
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums" style={{ color }} title={`${delta >= 0 ? "+" : ""}${money(delta)} vs previous`}>
      <Icon className="h-3 w-3" />{Math.abs(pct)}%
    </span>
  );
}

// A compact channel <Select> reused inline + in the manage dialog.
function ChannelSelect({ row, saving, onPick, testId }) {
  const current = resolveAgencyChannel(row);
  const overridden = isChannelOverridden(row);
  return (
    <div className="flex items-center gap-1.5">
      {overridden && <Badge variant="secondary" className="h-5 shrink-0 px-1.5 text-[9px] uppercase tracking-wide" data-testid="override-badge">Manual</Badge>}
      <Select value={current} disabled={saving} onValueChange={(v) => onPick(row, v === AUTO ? "" : v)}>
        <SelectTrigger className="h-8 w-40" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {AGENCY_CHANNEL_ORDER.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          {overridden && <SelectSeparator />}
          {overridden && (
            <SelectItem value={AUTO} data-testid="reset-to-auto">
              <span className="flex items-center gap-1.5"><RotateCcw className="h-3 w-3" /> Reset to auto</span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SpendsInsights({ rows, onDrill, drill, isAdmin, onReload }) {
  const { money } = useCurrency();
  const [open, setOpen] = useState(true);
  const [dim, setDim] = useState("cost_bucket");
  const [visPerf, setVisPerf] = useState("all");
  const [gran, setGran] = useState("month"); // month | quarter (Period Compare)
  const [manageOpen, setManageOpen] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [ovSearch, setOvSearch] = useState("");

  const base = useMemo(() => {
    const r = rows || [];
    if (visPerf === "all") return r;
    return r.filter((x) => x.vis_perf === visPerf);
  }, [rows, visPerf]);

  const total = useMemo(() => base.reduce((s, r) => s + Number(r.amount || 0), 0), [base]);

  const visSplit = useMemo(() => {
    const src = rows || [];
    const v = src.filter((x) => x.vis_perf === "Visibility").reduce((s, r) => s + Number(r.amount || 0), 0);
    const p = src.filter((x) => x.vis_perf === "Performance").reduce((s, r) => s + Number(r.amount || 0), 0);
    return [{ name: "Visibility", value: v }, { name: "Performance", value: p }];
  }, [rows]);
  const visTotal = visSplit[0].value + visSplit[1].value;

  const breakdown = useMemo(() => {
    const keyFn = keyForDim(dim);
    const src = dim === "agency_channel" ? base.filter((r) => r.platform === "Agency") : base;
    return topNWithOthers(groupSum(src, keyFn), 12);
  }, [base, dim]);

  const byVertical = useMemo(() => orderBy(groupSum(base, (r) => r.vertical), VERTICAL_GRAPH_ORDER).filter((x) => x.spend > 0), [base]);
  const byPlatform = useMemo(() => topNWithOthers(groupSum(base, (r) => r.platform), 12), [base]);
  const agencyMix = useMemo(() => {
    const ag = base.filter((r) => r.platform === "Agency");
    return orderBy(groupSum(ag, (r) => resolveAgencyChannel(r)), AGENCY_CHANNEL_ORDER).filter((x) => x.spend > 0);
  }, [base]);
  const agencyTotal = agencyMix.reduce((s, x) => s + x.spend, 0);
  const dimLabel = DIMS.find((d) => d.key === dim)?.label || "";

  // ---- Period Compare -----------------------------------------------------
  const compare = useMemo(() => {
    const periods = orderedPeriods(base, gran);
    const curP = periods[periods.length - 1] || null;
    const prevP = periods[periods.length - 2] || null;
    if (!curP) return null;
    const keyFn = keyForDim(dim);
    const inPeriod = (p) => (dim === "agency_channel"
      ? base.filter((r) => r.platform === "Agency" && periodKey(r, gran) === p)
      : base.filter((r) => periodKey(r, gran) === p));
    const curG = Object.fromEntries(groupSum(inPeriod(curP), keyFn).map((x) => [x.name, x.spend]));
    const prevG = prevP ? Object.fromEntries(groupSum(inPeriod(prevP), keyFn).map((x) => [x.name, x.spend])) : {};
    const names = Array.from(new Set([...Object.keys(curG), ...Object.keys(prevG)]));
    const list = names
      .map((n) => ({ name: n, cur: curG[n] || 0, prev: prevP ? (prevG[n] || 0) : null, delta: (curG[n] || 0) - (prevG[n] || 0) }))
      .sort((a, b) => b.cur - a.cur)
      .slice(0, 8);
    const curTotal = Object.values(curG).reduce((s, v) => s + v, 0);
    const prevTotal = Object.values(prevG).reduce((s, v) => s + v, 0);
    return { curP, prevP, list, curTotal, prevTotal, hasPrev: !!prevP };
  }, [base, dim, gran]);

  // ---- Channel overrides --------------------------------------------------
  const agencyLines = useMemo(
    () => base.filter((r) => r.platform === "Agency").slice().sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0)),
    [base]);
  const overriddenCount = useMemo(() => agencyLines.filter(isChannelOverridden).length, [agencyLines]);
  const inlineLines = agencyLines.slice(0, 8);
  const dialogLines = useMemo(() => {
    const q = ovSearch.trim().toLowerCase();
    const src = q
      ? agencyLines.filter((r) => `${r.activity || ""} ${r.sub_line_item || ""} ${r.vendor || ""} ${r.project || ""}`.toLowerCase().includes(q))
      : agencyLines;
    return src.slice(0, 150);
  }, [agencyLines, ovSearch]);

  const doOverride = async (row, channel) => {
    try {
      setSavingId(row.id);
      await api.overrideAgencyChannel(row.id, channel);
      toast.success(channel ? `Re-mapped to ${channel}` : "Reset to auto-classification");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save the override");
    } finally {
      setSavingId(null);
    }
  };

  // ---- Drill wiring -------------------------------------------------------
  const fireDrill = (payload) => {
    if (!onDrill) return;
    if (!payload || !payload.value) return;
    if (payload.value === "Others") {
      toast.info("\u201cOthers\u201d is an aggregate bucket \u2014 refine with the ledger filters instead.");
      return;
    }
    onDrill(payload);
  };

  const OverridesButton = isAdmin ? (
    <Button variant="outline" size="sm" className="h-8" onClick={() => setManageOpen(true)} data-testid="manage-overrides-button">
      <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Overrides{overriddenCount ? ` (${overriddenCount})` : ""}
    </Button>
  ) : null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SectionCard testId="spends-insights"
        title="Spends Analytics"
        description="Executive breakdown of the filtered ledger — group spends by any dimension, refine by visibility vs performance, compare periods, override Agency channels, and click any bar to drill into the ledger."
        action={(
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup type="single" value={visPerf} onValueChange={(v) => v && setVisPerf(v)} className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5" data-testid="spends-insights-visperf">
              <ToggleGroupItem value="all" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="spends-insights-visperf-all">All</ToggleGroupItem>
              <ToggleGroupItem value="Visibility" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="spends-insights-visperf-visibility">Visibility</ToggleGroupItem>
              <ToggleGroupItem value="Performance" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="spends-insights-visperf-performance">Performance</ToggleGroupItem>
            </ToggleGroup>
            <Button variant="outline" size="sm" className="h-8" onClick={exportActiveView} data-testid="analytics-export-button">
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export
            </Button>
            {OverridesButton}
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="spends-insights-toggle">
                <ChevronDown className={"h-4 w-4 transition-transform duration-200 " + (open ? "rotate-180" : "")} />
              </Button>
            </CollapsibleTrigger>
          </div>
        )}>
        <CollapsibleContent>
          <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-3">
            {/* Group-by breakdown */}
            <Tile icon={BarChart3} title="Spends Breakdown" accent="hsl(var(--primary))" testId="tile-spends-breakdown"
              hint={`Grouped by ${dimLabel} \u00b7 ${money(total)} \u00b7 click a bar to drill the ledger`}
              action={(
                <Select value={dim} onValueChange={setDim}>
                  <SelectTrigger className="h-8 w-40" data-testid="spends-insights-groupby"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIMS.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              )}>
              <HBar rows={breakdown} total={total} accent="hsl(var(--primary))" gradId="si-grad-breakdown" money={money}
                testId="chart-spends-breakdown" height={Math.max(220, breakdown.length * 30)}
                onBarClick={(name) => fireDrill({ dim, value: name, label: dimLabel })} />
            </Tile>

            {/* Period Compare */}
            <Tile icon={BarChart3} title="Period Compare" accent="hsl(var(--info))" testId="tile-period-compare"
              hint={compare ? (compare.hasPrev ? `${compare.curP} vs ${compare.prevP} \u00b7 grouped by ${dimLabel}` : `Only ${compare.curP} in view`) : "No periods in the current view"}
              action={(
                <ToggleGroup type="single" value={gran} onValueChange={(v) => v && setGran(v)} className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5" data-testid="period-compare-gran">
                  <ToggleGroupItem value="month" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="period-gran-month">Month</ToggleGroupItem>
                  <ToggleGroupItem value="quarter" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="period-gran-quarter">Quarter</ToggleGroupItem>
                </ToggleGroup>
              )}>
              {!compare ? (
                <div className="py-6"><EmptyState title="No period data" message="Nothing to compare in the current view." /></div>
              ) : (
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-2" data-testid="period-compare-total">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{compare.curP} total</p>
                      <p className="num text-lg font-semibold tabular-nums">{money(compare.curTotal)}</p>
                    </div>
                    {compare.hasPrev ? (
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">vs {compare.prevP} ({money(compare.prevTotal)})</p>
                        <div className="flex justify-end"><DeltaBadge delta={compare.curTotal - compare.prevTotal} prev={compare.prevTotal} money={money} /></div>
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">no previous period</span>
                    )}
                  </div>
                  <div className="space-y-1" data-testid="period-compare-list">
                    {compare.list.map((r) => (
                      <div key={r.name} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5">
                        <span className="min-w-0 flex-1 truncate text-xs" title={r.name}>{r.name}</span>
                        <span className="num text-xs font-medium tabular-nums">{money(r.cur)}</span>
                        <span className="w-14 text-right"><DeltaBadge delta={r.delta} prev={r.prev} money={money} /></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Tile>

            {/* Vis vs Performance */}
            <Tile icon={Eye} title="Visibility vs Performance" accent="hsl(var(--chart-cat-3))" testId="tile-vis-perf"
              hint="Cost efficiency ratios use performance spends only">
              <div className="flex flex-col items-center gap-3 sm:flex-row">
                <div style={{ width: 150, height: 150 }} className="relative shrink-0" data-testid="chart-vis-perf">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={visSplit} dataKey="value" nameKey="name" innerRadius={46} outerRadius={72} paddingAngle={2} isAnimationActive={false}>
                        <Cell fill="hsl(var(--chart-cat-3))" />
                        <Cell fill="hsl(var(--chart-cat-1))" />
                      </Pie>
                      <Tooltip content={<ChartTooltip formatter={(v) => `${money(v)} \u00b7 ${visTotal ? Math.round((v / visTotal) * 100) : 0}%`} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="rounded-lg border bg-card p-2.5" data-testid="vis-perf-visibility">
                    <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "hsl(var(--chart-cat-3))" }} /><span className="flex-1 text-xs font-medium">Visibility</span><span className="num text-sm font-semibold">{money(visSplit[0].value)}</span></div>
                    <p className="pl-4 text-[10px] text-muted-foreground tabular-nums">{visTotal ? Math.round((visSplit[0].value / visTotal) * 100) : 0}% of spend</p>
                  </div>
                  <div className="rounded-lg border bg-card p-2.5" data-testid="vis-perf-performance">
                    <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "hsl(var(--chart-cat-1))" }} /><span className="flex-1 text-xs font-medium">Performance</span><span className="num text-sm font-semibold">{money(visSplit[1].value)}</span></div>
                    <p className="pl-4 text-[10px] text-muted-foreground tabular-nums">{visTotal ? Math.round((visSplit[1].value / visTotal) * 100) : 0}% of spend</p>
                  </div>
                </div>
              </div>
            </Tile>

            {/* Spend by Platform */}
            <Tile icon={MonitorSmartphone} title="Spend by Platform" accent="hsl(var(--chart-cat-5))" testId="tile-by-platform"
              hint="Where the money is deployed · click a bar to drill">
              <HBar rows={byPlatform} total={total} accent="hsl(var(--chart-cat-5))" gradId="si-grad-platform" money={money}
                testId="chart-spends-by-platform" height={Math.max(200, byPlatform.length * 30)}
                onBarClick={(name) => fireDrill({ dim: "platform", value: name, label: "Platform" })} />
            </Tile>

            {/* Spends by Vertical (restricted 8) */}
            <Tile icon={Layers} title="Spends by Vertical" accent="hsl(var(--chart-cat-4))" testId="tile-by-vertical"
              hint="ATL · BTL · Brand · Digital · Admin · Technology · CX · CXX · click to drill">
              <HBar rows={byVertical} total={total} accent="hsl(var(--chart-cat-4))" gradId="si-grad-vertical" money={money}
                testId="chart-spends-by-vertical" height={Math.max(200, byVertical.length * 34)}
                onBarClick={(name) => fireDrill({ dim: "vertical", value: name, label: "Vertical" })} />
            </Tile>

            {/* Agency Channel Mix */}
            <Tile icon={Zap} title="Agency Channel Mix" accent="hsl(var(--chart-cat-6))" testId="tile-agency-mix"
              hint={`Agency spend re-categorised \u2014 ${money(agencyTotal)} across ${agencyMix.length} channels \u00b7 click to drill`}>
              <HBar rows={agencyMix} total={agencyTotal} accent="hsl(var(--chart-cat-6))" gradId="si-grad-agency" money={money}
                testId="chart-agency-channel-mix" height={Math.max(220, agencyMix.length * 34)}
                onBarClick={(name) => fireDrill({ dim: "agency_channel", value: name, label: "Agency Channel" })} />
            </Tile>

            {/* Channel Overrides — inline (admin/leader only) */}
            {isAdmin && (
              <Tile icon={SlidersHorizontal} title="Channel Overrides" accent="hsl(var(--warning))" testId="tile-channel-overrides"
                hint={`Re-map an Agency line when auto-classification is off \u00b7 top ${inlineLines.length} by spend`}
                action={agencyLines.length > inlineLines.length ? (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setManageOpen(true)} data-testid="manage-overrides-inline">
                    Manage all ({agencyLines.length})
                  </Button>
                ) : null}>
                {inlineLines.length === 0 ? (
                  <div className="py-6"><EmptyState title="No Agency lines" message="No Agency spends in the current view." /></div>
                ) : (
                  <div className="space-y-1.5" data-testid="override-inline-list">
                    {inlineLines.map((r) => (
                      <div key={r.id} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5" data-testid={`override-inline-row-${r.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium" title={r.activity || r.sub_line_item}>{r.activity || r.sub_line_item || "\u2014"}</p>
                          <p className="truncate text-[10px] text-muted-foreground">{r.vendor} · {r.spend_month} · {money(r.amount)}</p>
                        </div>
                        <ChannelSelect row={r} saving={savingId === r.id} onPick={doOverride} testId={`override-inline-select-${r.id}`} />
                      </div>
                    ))}
                  </div>
                )}
              </Tile>
            )}

            {/* Agency channel legend / definitions */}
            <Tile icon={Building2} title="Agency channels explained" accent="hsl(var(--info))" testId="tile-agency-legend"
              hint="How each Agency channel is classified">
              <div className="space-y-1.5">
                {AGENCY_CHANNEL_ORDER.map((name, i) => {
                  const row = agencyMix.find((x) => x.name === name);
                  if (!row) return null;
                  return (
                    <div key={name} className="flex items-start gap-2 rounded-lg border bg-card p-2" data-testid={`agency-legend-${name.replace(/[^a-zA-Z]+/g, '-')}`}>
                      <span className="mt-0.5 h-6 w-1.5 shrink-0 rounded-full" style={{ background: seriesColor(i) }} />
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center justify-between gap-2 text-xs font-medium">{name}<span className="num font-semibold">{money(row.spend)}</span></p>
                        <p className="text-[10px] text-muted-foreground">{AGENCY_CHANNEL_HINT[name]}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Tile>
          </div>

          {drill && (
            <div className="flex items-center gap-2 border-t px-4 py-2.5 text-xs text-muted-foreground" data-testid="insights-drill-note">
              <MousePointerClick className="h-3.5 w-3.5" />
              Ledger below is filtered to <span className="font-medium text-foreground">{drill.label || drill.dim}: {drill.value}</span>. Use the chip near the table to clear it.
            </div>
          )}
        </CollapsibleContent>
      </SectionCard>

      {/* Manage Overrides dialog */}
      {isAdmin && (
        <Dialog open={manageOpen} onOpenChange={setManageOpen}>
          <DialogContent className="max-w-2xl" data-testid="manage-overrides-dialog">
            <DialogHeader>
              <DialogTitle>Manage Agency channel overrides</DialogTitle>
              <DialogDescription>
                Re-map any Agency line to the correct channel. Overrides are saved instantly and always win over auto-classification.
              </DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={ovSearch} onChange={(e) => setOvSearch(e.target.value)} placeholder="Search activity, vendor or project…" className="pl-8" data-testid="override-search" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Showing {dialogLines.length} of {agencyLines.length} Agency lines{overriddenCount ? ` \u00b7 ${overriddenCount} overridden` : ""}
            </p>
            <ScrollArea className="h-[420px] pr-3" data-testid="override-dialog-list">
              <div className="space-y-1.5">
                {dialogLines.length === 0 ? (
                  <div className="py-8"><EmptyState title="No matches" message="No Agency lines match your search." /></div>
                ) : dialogLines.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5" data-testid={`override-dialog-row-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium" title={r.activity || r.sub_line_item}>{r.activity || r.sub_line_item || "\u2014"}</p>
                      <p className="truncate text-[10px] text-muted-foreground">{r.vendor} · {r.project} · {r.spend_month} · {money(r.amount)}</p>
                    </div>
                    <ChannelSelect row={r} saving={savingId === r.id} onPick={doOverride} testId={`override-dialog-select-${r.id}`} />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </Collapsible>
  );

  // ---- Export the current active view (single sheet) ----------------------
  function exportActiveView() {
    try {
      const share = (v) => (total ? +((v / total) * 100).toFixed(1) : 0);
      const data = breakdown.map((b) => ({
        [dimLabel]: b.name,
        Spend: Math.round(b.spend),
        "Share %": share(b.spend),
        "Line Items": b.count,
      }));
      data.push({ [dimLabel]: "TOTAL", Spend: Math.round(total), "Share %": 100, "Line Items": base.length });
      const ws = XLSX.utils.json_to_sheet(data);
      ws["!cols"] = [{ wch: 26 }, { wch: 14 }, { wch: 10 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, (dimLabel || "View").slice(0, 31));
      const refine = visPerf === "all" ? "all" : visPerf.toLowerCase();
      const fname = `spends-${dim}-${refine}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Exported ${data.length - 1} rows to Excel`);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    }
  }
}
